import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./VerifyOTP.scss";
import { Box, Button, Grid, Input, Link } from "@material-ui/core";
import {
  useNetworkStatus,
  useTranslations,
} from "examsbook-react-sdk/custom-hooks";
import LearnifyIcon from "../../assets/images/LearnifyLogoDark.png";
import { useDispatch, useSelector } from "react-redux";
import { ColorsConstant } from "../../constants/colorConstant";
import { useHistory, useLocation } from "react-router-dom";
import { verifyPasswordViaOtp } from "examsbook-react-sdk/actions";
import { resendOtpVerify } from "examsbook-react-sdk/services/userService";
import { getSettingsAction, setSubdomain } from "examsbook-react-sdk/actions/getSettingsAction";

const VerifyOTP = () => {
  const location = useLocation();
  const userIdentifier = location?.state?.userIdentifier;
  const dispatch = useDispatch();
  const history = useHistory();
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [countdown, setCountdown] = useState(59);
  const [attempts, setAttempts] = useState(0);
  const [subDomainName, setSubDomainName] = useState("");
  const inputRefs = useRef([]);
  const [settingsData, setSettingsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const subdomain =
    useSelector((state) => state?.subdomainReducer?.subdomain) || subDomainName;

  useEffect(() => {
    if(subdomain && subdomain !== null) {
        getSettingsApiData();
    }
  }, [subdomain]);

  const getSettingsApiData = async () => {
    try {
      await dispatch(getSettingsAction(subdomain)).then((res) => {
        setSettingsData(res?.data);
        setIsLoading(true);
      });
    } catch (error) {
      console.log("Error -", error.message);
    }
  };

  const isOnline = useNetworkStatus();
  const t = useTranslations();

  const getSubdomain = useCallback(() => {
    const host = window.location.host;
    const domainParts = host.split(".");
    if (process.env.REACT_APP_TENANT_DOMAIN) {
      setSubDomainName(process.env.REACT_APP_TENANT_DOMAIN);
      dispatch(setSubdomain(process.env.REACT_APP_TENANT_DOMAIN));
    } else if (domainParts.length > 3) {
      const domainName = domainParts[0];
      setSubDomainName(domainName);
      dispatch(setSubdomain(domainName));
    }
  }, [dispatch]);

  useEffect(() => {
    getSubdomain();
  }, [getSubdomain]);

  const [bgColor, setBgColor] = useState(ColorsConstant.defaultBlue);
  const [textColor, setTextColor] = useState(ColorsConstant.white);
  const [dynamicLogo, setDynamicLogo] = useState(LearnifyIcon);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  useEffect(() => {
    const dynamicLogos = settingsData?.data?.[0]?.branding?.logos?.webAndAppLogo?.imagePath;
    setDynamicLogo(dynamicLogos ? dynamicLogos : LearnifyIcon);
    setBgColor(settingsData?.data?.[0] ? settingsData?.data?.[0]?.branding?.backgroundAndTextTheme?.primaryBackgroundColor : ColorsConstant.defaultBlue);
    setTextColor(settingsData?.data?.[0] ? settingsData?.data?.[0]?.branding?.backgroundAndTextTheme?.primaryTextColor : ColorsConstant.white);
  },[isLoading, settingsData]);

  const isOtpComplete = otp.every((digit) => digit !== "");

  const handleOtpChange = (element, index) => {
    document.getElementById("resend-code-message").style.display = "none";
    const value = element.value.replace(/[^0-9]/g, "");
    const newOtp = [...otp];

    if (value.length === 1) {
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (value === "") {
      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];

      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const errorElement = document.getElementById("resend-code-message");
      const enteredOtp = otp.join("");

      if (!userIdentifier || !enteredOtp) return;

      if (countdown === 0) {
        errorElement.innerHTML = `<div class="messageBox errorMsg">OTP expired. Please click Resend Code to receive a new one.</div>`;
        errorElement.style.display = "block";
        return;
      }

      const data = {
        otp: enteredOtp,
        subdomain,
        ...(userIdentifier.includes("@")
          ? { email: userIdentifier?.toLowerCase() }
          : { mobileNumber: userIdentifier }),
      };

      const res = await dispatch(verifyPasswordViaOtp(data));
      setAttempts(res.data.attempts);

      if (res.status === 200) {
        history.push("/change-password", {
          viaVerificationOtp: true,
          userIdentifier,
          enteredOtp,
        });
      } else {
        errorElement.innerHTML = `<div class="messageBox errorMsg">${res.data.message}</div>`;
        errorElement.style.display = "block";
      }
      setTimeout(() => {
        errorElement.style.display = "none";
      }, 1000);
    },
    [otp, userIdentifier, countdown, subdomain, dispatch, history]
  );

  const handleResendCode = async (event) => {
    event.preventDefault();
    const successMsgBox = document.getElementById("resend-code-message");

    if (!userIdentifier) return;

    try {
      const payload = {
        subdomain,
        ...(userIdentifier.includes("@")
          ? { email: userIdentifier?.toLowerCase() }
          : { mobileNumber: userIdentifier }),
      };

      const res = await resendOtpVerify(payload);
      if (res.status === 200) {
        setAttempts(0);
        setCountdown(59);
        setOtp(new Array(6).fill(""));
        successMsgBox.innerHTML = `<div class="messageBox successMsg">Verification code resent successfully.</div>`;
      } else {
        successMsgBox.innerHTML = `<div class="messageBox errorMsg">${
          res.error || "Failed to resend code."
        }</div>`;
      }
    } catch (error) {
      successMsgBox.innerHTML = `<div class="messageBox errorMsg">Something went wrong. Please try again later.</div>`;
    }

    successMsgBox.style.display = "block";
    setTimeout(() => {
      successMsgBox.style.display = "none";
    }, 1000);
  };

  const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <Grid className="forgot-password-parent-div">
      <Grid className="forgot-password-child-div">
        {!isLoading && dynamicLogo && (
          <Box
            component="img"
            id="login-img-learn"
            className="forgot-password-logo"
            src={dynamicLogo}
            alt="Learnify"
          />
        )}
      </Grid>

      {!isOnline && (
        <Box className="form-group error-msg" style={{ display: "block" }}>
          {t("No internet connection!")}
        </Box>
      )}

      <Grid className="forgot-password-right-side-box">
        <Box className="mdl-card__supporting-text input-fields">
          <Box className="forget-title" style={{ color: `${bgColor}` }}>
            {t("Verification Code")}
          </Box>
          <Box>
            <Box component="p" className="custom-sub-title">
              {t(
                "Please enter the 6-digit verification code we’ve sent to your email address or mobile number."
              )}
            </Box>
          </Box>

          <form onSubmit={(event) => handleSubmit(event)}>
            <Box
              className="otp-section"
              display="flex"
              flexDirection="column"
              alignItems="center"
              mt={3}
            >
              <Box
                className="otp-input-boxes"
                display="flex"
                justifyContent="center"
              >
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    type="text"
                    inputProps={{
                      maxLength: 1,
                      style: { textAlign: "center" },
                    }}
                    className="otp-box"
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={() => inputRefs.current[index]?.select()}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    style={{
                      width: 45,
                      height: 45,
                      textAlign: "center",
                      marginRight: index < 5 ? 8 : 0,
                      fontSize: 20,
                      border: "1px solid #ccc",
                      borderRadius: 5,
                      backgroundColor: "#fff",
                    }}
                  />
                ))}
              </Box>

              <Box className="countdown-timer" mt={2} textAlign="center">
                {countdown > 0 ? (
                  <>
                    Code Expires In: <strong>{formatTime(countdown)}</strong>
                  </>
                ) : (
                  <strong style={{ color: "red" }}>Code Expired</strong>
                )}
              </Box>
              <Box mb={5} mt={3} textAlign="center">
                Didn’t Receive Code?{" "}
                <Link
                  href="#"
                  onClick={(event) => {
                    if (countdown > 0) {
                      event.preventDefault();
                      return;
                    }
                    handleResendCode(event);
                  }}
                  style={{
                    color: countdown === 0 ? bgColor : "grey",
                    textDecoration: "underline",
                    pointerEvents: countdown === 0 ? "auto" : "none",
                    cursor: countdown === 0 ? "pointer" : "not-allowed",
                  }}
                >
                  {t("Resend Code")}
                </Link>
                <div id="resend-code-message" style={{ fontSize: 14 }}></div>
              </Box>
            </Box>

            <Button
              type="submit"
              className="send-reset-link-button"
              disabled={!isOtpComplete || attempts >= 3}
              style={{
                backgroundColor:
                  isOtpComplete && attempts < 3 ? bgColor : "#ccc",
                color: isOtpComplete && attempts < 3 ? textColor : "#666",
                padding: "10px 20px",
                cursor:
                  isOtpComplete && attempts < 3 ? "pointer" : "not-allowed",
              }}
            >
              {t("Continue")}
            </Button>

            {attempts >= 3 && (
              <Box
                className="error-msg"
                style={{ color: "red", textAlign: "center", marginTop: 10 }}
              >
                {t(
                  "You have reached the maximum number of attempts. Please wait till code expired!"
                )}
              </Box>
            )}

            <Box className="back-login" mt={2} textAlign="center">
              <Link href="/login" style={{ color: bgColor }}>
                {t("Back To Login")}
              </Link>
            </Box>
          </form>
        </Box>
      </Grid>
    </Grid>
  );
};

export default VerifyOTP;
